import React from 'react'

import { SortOrder } from '@/types/common'

import { SortDirection } from '../../utils/constants'

import { DEFAULT_COLUMN_WIDTH, TableColumn } from './TableHead'

export enum FreezePosition {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum Alignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export type StickyOffsets = {
  leftOffsets: string[]
  rightOffsets: string[]
}

type WidthValue = string | number | undefined

function parseWidth(width: WidthValue): number {
  if (!width) return DEFAULT_COLUMN_WIDTH
  if (typeof width === 'number') return width

  const regex = /^(\d+(?:\.\d+)?)(px|rem|em)?$/

  const match = regex.exec(width)
  if (match) {
    const value = Number.parseFloat(match[1])
    const unit = match[2]

    if (unit === 'rem' || unit === 'em') {
      return value * 16
    }
    return value
  }

  return DEFAULT_COLUMN_WIDTH
}

export function getStickyOffsets<T extends object = Record<string, unknown>>(
  columns: TableColumn<T>[]
): StickyOffsets {
  const leftOffsets: string[] = []
  const rightOffsets: string[] = []
  let leftWidth = 0
  let rightWidth = 0

  columns.forEach((col, i) => {
    if (col.isFrozen && col.freezePosition === FreezePosition.LEFT) {
      leftOffsets[i] = `${leftWidth}px`
      leftWidth += parseWidth(col.width)
    } else {
      leftOffsets[i] = ''
    }
  })

  for (let i = columns.length - 1; i >= 0; i--) {
    const col = columns[i]
    if (col.isFrozen && col.freezePosition === FreezePosition.RIGHT) {
      rightOffsets[i] = `${rightWidth}px`
      rightWidth += parseWidth(col.width)
    } else {
      rightOffsets[i] = ''
    }
  }

  return { leftOffsets, rightOffsets }
}

export function getTextContent(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }
  if (React.isValidElement(node) && node.props.children) {
    return getTextContent(node.props.children)
  }
  if (Array.isArray(node)) {
    return node.map(getTextContent).join('')
  }
  return ''
}

export const getStickyPositionStyle = (
  freezePosition: FreezePosition | undefined,
  leftOffset: number,
  rightOffset: number
): { left?: number; right?: number } => {
  if (freezePosition === FreezePosition.LEFT) {
    return { left: leftOffset }
  }

  if (freezePosition === FreezePosition.RIGHT) {
    return { right: rightOffset }
  }

  return {}
}

export const getNextSortOrder = (
  isActive: boolean,
  currentOrder: SortOrder
): { order: SortOrder; shouldReset: boolean } => {
  if (!isActive) {
    return { order: SortDirection.DESC, shouldReset: false }
  }

  if (currentOrder === SortDirection.DESC) {
    return { order: SortDirection.ASC, shouldReset: false }
  }

  return { order: null, shouldReset: true }
}

export const isLastLeftFrozenColumn = (
  columns: { isFrozen?: boolean; freezePosition?: FreezePosition }[],
  currentIndex: number
): boolean => {
  const currentCol = columns[currentIndex]
  if (
    !currentCol?.isFrozen ||
    currentCol.freezePosition !== FreezePosition.LEFT
  ) {
    return false
  }

  // Check if there are any more left-frozen columns after this one
  for (let i = currentIndex + 1; i < columns.length; i++) {
    if (
      columns[i]?.isFrozen &&
      columns[i].freezePosition === FreezePosition.LEFT
    ) {
      return false
    }
  }

  return true
}

export const isFirstRightFrozenColumn = (
  columns: { isFrozen?: boolean; freezePosition?: FreezePosition }[],
  currentIndex: number
): boolean => {
  const currentCol = columns[currentIndex]
  if (
    !currentCol?.isFrozen ||
    currentCol.freezePosition !== FreezePosition.RIGHT
  ) {
    return false
  }

  // Check if there are any right-frozen columns before this one
  for (let i = 0; i < currentIndex; i++) {
    if (
      columns[i]?.isFrozen &&
      columns[i].freezePosition === FreezePosition.RIGHT
    ) {
      return false
    }
  }

  return true
}
